import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from "@/components/ui/SossietyDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCreateTraining, useUpdateTraining } from '@/hooks/useTrainings';
import { format } from 'date-fns';
import { CalendarIcon, Clock, MapPin, User, FileText, Trophy, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { AcademyAutocomplete } from '@/components/ui/AcademyAutocomplete';
import { InstructorAutocomplete } from '@/components/ui/InstructorAutocomplete';
import { InstructorDisplay } from '@/components/ui/InstructorDisplay';
import { PartnerAutocomplete } from '@/components/ui/PartnerAutocomplete';
import { PartnerDisplay } from '@/components/ui/PartnerDisplay';
import { Switch } from "@/components/ui/switch";
import { SossietyDialogHeader, SossietyDialogClose } from '@/components/ui/SossietyDialogHeader';
import { TrainingPartner } from '@/types/trainings';
import { useToast } from '@/hooks/use-toast';
import { DialogTitle } from '@/components/ui/dialog';
import { useI18n } from '@/contexts/I18nContext';
import { useAgendaConfig } from '@/hooks/useAgendaConfig';

interface AddTrainingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTrainingCreated: () => void;
  selectedDate?: Date;
  onOpenNotesModal?: (training: any) => void;
  // Edit mode props
  mode?: 'create' | 'edit';
  trainingToEdit?: any;
  onTrainingUpdated?: () => void;
}

interface TrainingForm {
  training_type: 'Group Class' | 'Private Class'; // New: toggle field
  category: string; // New: optional category field (was 'type')
  modality: 'Gi' | 'No-Gi'; // Keep as 'No-Gi' to match existing database
  date: string;
  time: string;
  duration: number;
  academy_id: string;
  instructor_id: string | undefined;
  instructor_name: string | undefined;
}

interface Academy {
  id: string;
  name: string;
}

interface Instructor {
  id: string;
  name: string;
  full_name?: string; // Add full_name as optional
  username?: string;
  profile_picture_url?: string;
  belt_rank?: string;
}

// Updated category options (removed Regular Class and Private Class)
const TRAINING_CATEGORIES = [
  'Conditioning',
  'Rolling',
  'Open Mat',
  'Drills',
  'Competition Prep',
  'Other'
];

// Time options in 30-minute intervals
const TIME_OPTIONS = [
  '12:00 AM', '12:30 AM',
  '1:00 AM', '1:30 AM',
  '2:00 AM', '2:30 AM',
  '3:00 AM', '3:30 AM',
  '4:00 AM', '4:30 AM',
  '5:00 AM', '5:30 AM',
  '6:00 AM', '6:30 AM',
  '7:00 AM', '7:30 AM',
  '8:00 AM', '8:30 AM',
  '9:00 AM', '9:30 AM',
  '10:00 AM', '10:30 AM',
  '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM',
  '1:00 PM', '1:30 PM',
  '2:00 PM', '2:30 PM',
  '3:00 PM', '3:30 PM',
  '4:00 PM', '4:30 PM',
  '5:00 PM', '5:30 PM',
  '6:00 PM', '6:30 PM',
  '7:00 PM', '7:30 PM',
  '8:00 PM', '8:30 PM',
  '9:00 PM', '9:30 PM',
  '10:00 PM', '10:30 PM',
  '11:00 PM', '11:30 PM'
];

// Helper function to format time input to AM/PM format
const formatTimeToAMPM = (timeInput: string): string | null => {
  
  if (!timeInput) return null;
  
  // If already in AM/PM format, return as is
  if (timeInput.includes('AM') || timeInput.includes('PM')) {
    return timeInput;
  }
  
  // Handle 24-hour format (HH:MM)
  const hour24Regex = /^(\d{1,2}):(\d{2})$/;
  const hour24Match = timeInput.match(hour24Regex);
  
  if (hour24Match) {
    const hours = parseInt(hour24Match[1]);
    const minutes = hour24Match[2];
    
    if (hours >= 0 && hours <= 23) {
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      const result = `${displayHour}:${minutes} ${ampm}`;
      return result;
    }
  }
  
  // Try to parse various formats
  const timeRegex = /^(\d{1,2}):?(\d{0,2})\s*(am|pm)?$/i;
  const match = timeInput.match(timeRegex);
  
  if (match) {
    let hours = parseInt(match[1]);
    let minutes = match[2] || '00';
    const ampm = match[3];
    
    // Validate hours and minutes
    if (hours < 1 || hours > 12) return null;
    if (minutes && (parseInt(minutes) < 0 || parseInt(minutes) > 59)) return null;
    
    // Ensure minutes is 2 digits
    if (minutes.length === 1) minutes = '0' + minutes;
    if (minutes.length === 0) minutes = '00';
    
    // If no AM/PM provided, try to guess based on common training times
    let finalAmPm = ampm;
    if (!finalAmPm) {
      // Most trainings are in the evening, but morning classes exist too
      // Default to PM for times 6-11, AM for 12 and 1-5
      if (hours >= 6 && hours <= 11) {
        finalAmPm = 'PM';
      } else {
        finalAmPm = 'AM';
      }
    }
    
    const result = `${hours}:${minutes} ${finalAmPm.toUpperCase()}`;
    return result;
  }
  
  return null;
};

export const AddTrainingModal = ({ 
  isOpen, 
  onClose, 
  onTrainingCreated, 
  selectedDate, 
  onOpenNotesModal,
  mode = 'create',
  trainingToEdit,
  onTrainingUpdated
}: AddTrainingModalProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t, currentLanguage } = useI18n();
  // Removed agenda config - not needed for basic training creation
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [academies, setAcademies] = useState<Academy[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [createdTraining, setCreatedTraining] = useState<any>(null);
  const [openTypeSelect, setOpenTypeSelect] = useState(false);

  const createTrainingMutation = useCreateTraining();
  const updateTrainingMutation = useUpdateTraining();

  const [form, setForm] = useState<TrainingForm>({
    training_type: 'Group Class', // Default to Group Class
    category: '', // Default to empty
    modality: 'Gi',
    date: '',
    time: '',
    duration: 60,
    academy_id: '',
    instructor_id: undefined,
    instructor_name: undefined
  });

  // Separate state for academy display value
  const [academyValue, setAcademyValue] = useState<{ id: string; name: string } | ''>('');
  const [academyLoading, setAcademyLoading] = useState(false);
  const [instructorValue, setInstructorValue] = useState<Instructor | null>(null);

  // Load all academies when modal opens and auto-fill for new trainings
  useEffect(() => {
    if (isOpen && user) {
      loadUserAcademies();
    }
  }, [isOpen, user]);

  // Clear form when modal opens in create mode
  useEffect(() => {
    if (isOpen && mode === 'create') {
      // Reset form for new training
      setForm({
        training_type: 'Group Class',
        category: '',
        modality: 'Gi',
        date: '', // Empty date field for new training
        time: '', // Empty time field for new training
        duration: 60,
        academy_id: '',
        instructor_id: undefined,
        instructor_name: undefined
      });
      setAcademyValue('');
      setInstructorValue(null);
      setStep('form');
    }
  }, [isOpen, mode, selectedDate]);

  // Reset step when modal opens in edit mode
  useEffect(() => {
    if (isOpen && mode === 'edit') {
      setStep('form');
      setCreatedTraining(null);
    }
  }, [isOpen, mode]);

  // Debug form changes
  useEffect(() => {
    // console.log('🔄 AddTrainingModal - Form state changed:', {
    //   instructor_id: form.instructor_id,
    //   instructor_name: form.instructor_name,
    //   instructorValue: instructorValue
    // });
  }, [form.instructor_id, form.instructor_name, instructorValue]);

  // Clear instructor state when modal opens
  useEffect(() => {
    if (isOpen && mode === 'edit') {
      // console.log('🔄 AddTrainingModal - Modal opened in edit mode, clearing instructor state');
      setForm(prev => ({ 
        ...prev, 
        instructor_id: undefined,
        instructor_name: undefined
      }));
      setInstructorValue(null);
    }
  }, [isOpen, mode]);

  // Load training details when modal opens or training changes
  useEffect(() => {
    // console.log('🔄 AddTrainingModal - useEffect triggered:', { isOpen, mode, trainingToEdit });
    
    if (isOpen) {
      if (mode === 'create') {
        // console.log('🔄 AddTrainingModal - Creating new training, resetting form');
        // Reset form for new training
        setForm({
          training_type: 'Group Class',
          category: '',
          modality: 'Gi',
          date: '', // Empty date field for new training
          time: '', // Empty time field for new training
          duration: 60,
          academy_id: '',
          instructor_id: undefined,
          instructor_name: undefined
        });
        setAcademyValue(null);
        setInstructorValue(null);
        setStep('form');
        setCreatedTraining(null);
      } else if (mode === 'edit' && trainingToEdit) {
        // console.log('🔄 AddTrainingModal - Loading training details for edit:', trainingToEdit);
        
        // Populate form with training data
        setForm({
          training_type: trainingToEdit.training_type || 'Group Class',
          category: trainingToEdit.category || '',
          modality: trainingToEdit.modality || 'Gi',
          date: trainingToEdit.start_time ? format(new Date(trainingToEdit.start_time), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
          time: trainingToEdit.time ? formatTimeToAMPM(trainingToEdit.time) || '6:00 PM' : '6:00 PM',
          duration: trainingToEdit.duration || 60,
          academy_id: trainingToEdit.academy_id || '',
          instructor_id: trainingToEdit.instructor_id || undefined,
          instructor_name: trainingToEdit.instructor_name || undefined
        });
        
        // Load academy and instructor details for display
        loadTrainingDetails(trainingToEdit);
      }
    }
  }, [isOpen, selectedDate, mode, trainingToEdit]);

  const loadTrainingDetails = async (training: any) => {
    // console.log('🔄 AddTrainingModal - loadTrainingDetails called with training:', training);
    try {
      // Load academy details
      if (training.academy_id) {
        const { data: academyData, error: academyError } = await supabase
          .from('academies')
          .select('id, name')
          .eq('id', training.academy_id)
          .single();

        if (!academyError && academyData) {
          setAcademies(prev => {
            const exists = prev.find(a => a.id === academyData.id);
            return exists ? prev : [...prev, academyData];
          });
          // Set the academy value for display
          setAcademyValue({ id: academyData.id, name: academyData.name });
        }
      }

      // Always fetch the latest instructor data from the database
      // console.log('🔄 AddTrainingModal - Fetching latest instructor data for training:', training.id);
      
      // Add a small delay to ensure database changes are propagated
      await new Promise(resolve => setTimeout(resolve, 500));
      
      try {
        // Use local Supabase functions URL in development
        const baseUrl = import.meta.env.DEV
          ? 'http://127.0.0.1:54321'
          : import.meta.env.VITE_SUPABASE_URL;

        const response = await fetch(`${baseUrl}/functions/v1/get_training_instructor`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            training_id: training.id,
            _t: Date.now() // Cache busting parameter
          })
        });

        // console.log('🔄 AddTrainingModal - API response status:', response.status);
        if (response.ok) {
          const result = await response.json();
          // console.log('🔄 AddTrainingModal - API result:', result);
          if (result.instructor) {
            // console.log('🔄 AddTrainingModal - Setting instructor value:', result.instructor);
            // Set the instructor value for display
            const instructorValue = { 
              id: result.instructor.instructor_user_id || result.instructor.id, 
              name: result.instructor.name || result.instructor.manual_name, 
              username: result.instructor.username || '',
              profile_picture_url: result.instructor.avatar_url,
              belt_rank: result.instructor.belt_rank
            };
            setInstructorValue(instructorValue);
            // Also update the form state
            setForm(prev => ({ 
              ...prev, 
              instructor_id: result.instructor.instructor_user_id || result.instructor.id,
              instructor_name: result.instructor.name || result.instructor.manual_name
            }));
          } else {
            // console.log('🔄 AddTrainingModal - No instructor found, clearing form');
            // No instructor found, clear the form
            setForm(prev => ({ 
              ...prev, 
              instructor_id: undefined,
              instructor_name: undefined
            }));
            setInstructorValue(null);
          }
        } else {
          // console.log('🔄 AddTrainingModal - API error, clearing form');
          // API error, clear the form
          setForm(prev => ({ 
            ...prev, 
            instructor_id: undefined,
            instructor_name: undefined
          }));
          setInstructorValue(null);
        }
      } catch (error) {
        // console.error('Error loading instructor details:', error);
        // Error occurred, clear the form
        setForm(prev => ({ 
          ...prev, 
          instructor_id: undefined,
          instructor_name: undefined
        }));
        setInstructorValue(null);
      }
    } catch (error) {
      // console.error('Error loading training details:', error);
    }
  };

  const loadUserAcademies = async () => {
    setAcademyLoading(true);
    try {
      const { data: academiesData, error: academiesError } = await supabase
        .from('academies')
        .select('id, name')
        .order('name');

      if (academiesError) throw academiesError;
      setAcademies(academiesData || []);
      
      // Load user's main academy
      if (user) {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('main_academy_id')
          .eq('id', user.id)
          .single();

        if (!profileError && profileData?.main_academy_id) {
          const userAcademy = academiesData?.find(academy => academy.id === profileData.main_academy_id);
          if (userAcademy) {
            setAcademyValue(userAcademy);
            setForm(prev => ({ ...prev, academy_id: userAcademy.id }));
          }
        }
      }
    } catch (error) {
      // console.error('Error loading academies:', error);
    } finally {
      setAcademyLoading(false);
    }
  };

  const isFormValid = () => {
    // In edit mode, we need to check if there are any changes to the form
    if (mode === 'edit' && trainingToEdit) {
      // Check if any field has changed from the original training
      const hasChanges = 
        form.training_type !== trainingToEdit.training_type ||
        form.modality !== trainingToEdit.modality ||
        form.category !== trainingToEdit.category ||
        form.duration !== trainingToEdit.duration ||
        form.academy_id !== trainingToEdit.academy_id ||
        form.instructor_id !== trainingToEdit.instructor_id ||
        form.instructor_name !== trainingToEdit.instructor_name ||
        form.date !== (trainingToEdit.start_time ? format(new Date(trainingToEdit.start_time), 'yyyy-MM-dd') : '') ||
        form.time !== trainingToEdit.time;
      
      return hasChanges;
    }
    
    // In create mode, check if required fields are filled
    const valid = form.date !== '' && form.time !== '';
    return valid;
  };

  const convertTimeTo24Hour = (timeString: string) => {
    const [time, period] = timeString.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    
    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }
    // 12 PM deve permanecer 12 (meio-dia)
    // 12 AM deve se tornar 0 (meia-noite)
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async () => {
    if (!isFormValid() || !user) return;

    try {

      // In edit mode, if form.time is empty, use the original training time
      let timeToUse = form.time;
      // console.log('🔄 AddTrainingModal - Debug time extraction:', {
      //   mode,
      //   formTime: form.time,
      //   trainingToEdit: trainingToEdit,
      //   trainingToEditStartTime: trainingToEdit?.start_time
      // });
      
      if (mode === 'edit' && (!form.time || form.time === '') && trainingToEdit) {
        // Convert the original training time to AM/PM format
        const originalStartTime = new Date(trainingToEdit.start_time);
        // console.log('🔄 AddTrainingModal - Original start time:', originalStartTime);
        
        const timeString = `${originalStartTime.getHours()}:${originalStartTime.getMinutes().toString().padStart(2, '0')}`;
        // console.log('🔄 AddTrainingModal - Time string:', timeString);
        
        timeToUse = formatTimeToAMPM(timeString) || '';
        // console.log('🔄 AddTrainingModal - Formatted time:', timeToUse);
      }
      
      // console.log('🔄 AddTrainingModal - Final timeToUse:', timeToUse);
      
      if (!timeToUse) {
        // console.error('No time available for training');
        return;
      }

      // In edit mode, if form.date is empty, use the original training date
      let dateToUse = form.date;
      if (mode === 'edit' && (!form.date || form.date === '') && trainingToEdit) {
        const originalStartTime = new Date(trainingToEdit.start_time);
        dateToUse = `${originalStartTime.getFullYear()}-${(originalStartTime.getMonth() + 1).toString().padStart(2, '0')}-${originalStartTime.getDate().toString().padStart(2, '0')}`;
      }
      
      if (!dateToUse) {
        // console.error('No date available for training');
        return;
      }

      const time24Hour = convertTimeTo24Hour(timeToUse);
      const [hours, minutes] = time24Hour.split(':').map(Number);
      
      const [year, month, day] = dateToUse.split('-').map(Number);
      
      // Create date in local timezone to preserve user's chosen time
      const startTime = new Date(year, month - 1, day, hours, minutes, 0, 0);
      const endTime = new Date(startTime);
      endTime.setMinutes(endTime.getMinutes() + form.duration);
      
      // Use local time directly - Supabase will handle timezone conversion
      const utcStartTime = startTime;
      const utcEndTime = endTime;

      if (mode === 'edit' && trainingToEdit) {
        // Update existing training
        const updateData = {
          title: form.training_type, // Use training_type as title
          start_time: utcStartTime.toISOString(),
          end_time: utcEndTime.toISOString(),
          modality: form.modality,
          type: 'Group Class' as const, // Always use 'Group Class' for type
          category: form.training_type, // Use training_type as category
          duration: form.duration,
          academy_id: form.academy_id || null // Allow null academy_id
        };

        await updateTrainingMutation.mutateAsync({
          trainingId: trainingToEdit.id,
          trainingData: updateData,
          instructorId: form.instructor_id || undefined
        });

        toast({
          title: "Training updated successfully!",
          description: "Your training has been updated.",
          duration: 2000,
        });

        onTrainingUpdated?.();
        setStep('form');
        setCreatedTraining(null);
        onClose();
      } else {
        // Create new training
      const data = await createTrainingMutation.mutateAsync({
        title: form.training_type, // Use training_type as title
        start_time: utcStartTime.toISOString(),
        end_time: utcEndTime.toISOString(),
        modality: form.modality,
        status: 'Scheduled' as const, // Always use 'Scheduled' for new trainings
        type: 'Group Class' as const, // Always use 'Group Class' for type
        category: form.training_type, // Use training_type as category
        duration: form.duration,
        academy_id: form.academy_id || null, // Allow null academy_id
        instructor_id: form.instructor_id,
        privacy: 'connections'
      });

      // Add instructor to training if provided
      if (form.instructor_id || form.instructor_name) {
        try {
            // Prepare instructor data - send only one field
            const instructorData: any = {
              training_id: data.id
            };

            if (form.instructor_id) {
              // If instructor_id is provided, use that (sOSSiety user)
              instructorData.instructor_user_id = form.instructor_id;
            } else if (form.instructor_name) {
              // If only name is provided, use manual name
              instructorData.manual_name = form.instructor_name;
            }

            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/add_training_instructor`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
              body: JSON.stringify(instructorData)
            });
            
            if (!response.ok) {
              const errorText = await response.text();
              // console.error('Error adding instructor:', response.statusText, errorText);
            } else {
              const result = await response.json();
              // console.log('Instructor added successfully:', result);
            }
        } catch (error) {
            // console.error('Error adding instructor to training:', error);
          }
      }

      setCreatedTraining(data);
      setStep('success');
      onTrainingCreated();
      }
    } catch (error) {
      // console.error('Error saving training:', error);
    }
  };

  const handleClose = () => {
    setStep('form');
    setForm({
      training_type: 'Group Class', // Default to Group Class
      category: '', // Default to empty
      modality: 'Gi',
      date: '',
      time: '',
      duration: 60,
      academy_id: '',
      instructor_id: undefined,
      instructor_name: undefined
    });
    setAcademyValue('');
    setInstructorValue(null);
    setStep('form');
    setCreatedTraining(null);
    onClose();
  };

  const handleSuccessButtonClick = () => {
    if (isCreatedTrainingPast() && onOpenNotesModal && createdTraining) {
      onOpenNotesModal(createdTraining);
    }
    handleClose();
  };

  const handleAcademyChange = (academy: { id: string; name: string } | string) => {
    if (typeof academy === 'object') {
      setForm(prev => ({ 
        ...prev, 
        academy_id: academy.id
      }));
      setAcademyValue(academy);
    } else {
      setForm(prev => ({ 
        ...prev, 
        academy_id: academy
      }));
      setAcademyValue(academy);
    }
  };

  const handleInstructorChange = (instructor: Instructor | string | null) => {
    // console.log('🔄 AddTrainingModal - handleInstructorChange called with:', instructor);
    
    if (instructor === null) {
      // console.log('🔄 AddTrainingModal - Clearing instructor');
      setForm(prev => ({ 
        ...prev, 
        instructor_id: undefined,
        instructor_name: undefined
      }));
      setInstructorValue(null);
      return;
    }

    if (typeof instructor === 'string') {
      // console.log('🔄 AddTrainingModal - Setting manual instructor:', instructor);
      setForm(prev => ({ 
        ...prev, 
        instructor_id: undefined,
        instructor_name: instructor
      }));
      setInstructorValue(instructor);
    } else {
      // console.log('🔄 AddTrainingModal - Setting instructor from object:', instructor);
      setForm(prev => ({ 
        ...prev, 
        instructor_id: instructor.id,
        instructor_name: instructor.name || instructor.full_name || ''
      }));
      setInstructorValue(instructor);
    }
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setForm(prev => ({ ...prev, duration: 0 }));
    } else {
      const numValue = parseInt(value);
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 480) {
        setForm(prev => ({ ...prev, duration: numValue }));
      }
    }
  };

  const isPastTraining = () => {
    if (!form.date || !form.time) return false;
    const time24Hour = convertTimeTo24Hour(form.time);
    const [hours, minutes] = time24Hour.split(':').map(Number);
    
    const [year, month, day] = form.date.split('-').map(Number);
    const trainingTime = new Date(year, month - 1, day, hours, minutes, 0, 0);
    
    return trainingTime < new Date();
  };

  const isCreatedTrainingPast = () => {
    if (!createdTraining) return false;
    const trainingTime = new Date(createdTraining.start_time);
    return trainingTime < new Date();
  };



  if (step === 'success' && createdTraining) {
    const trainingDate = new Date(createdTraining.start_time);
    const isPast = isCreatedTrainingPast();

    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="w-[90vw] max-w-xl bg-white rounded-2xl p-4">
          <div className="flex flex-col items-center justify-center text-center px-1 py-6">
            {/* Title */}
            <h3 className="text-xl font-semibold text-black mb-6 font-roboto">
              {t('modals.success')}!
            </h3>
            
            {/* Icon */}
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            
            {/* Content */}
            <div className="space-y-4 mb-8">
              <p className="text-gray-600">
                {t('calendar.training_added_successfully', { date: format(trainingDate, 'MMMM d, yyyy') })}
              </p>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="font-semibold text-black">
                  {createdTraining.title}
                </div>
                <div className="text-gray-600">
                  {format(trainingDate, 'MMMM d, yyyy')} at {format(trainingDate, 'h:mm a')}
                </div>
                <div className="text-gray-500 text-xs font-medium">
                  {createdTraining.modality}
                </div>
              </div>

              {isPast && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-blue-700 text-sm">
                    {t('calendar.training_past_redirect_notes')}
                  </p>
                </div>
              )}
            </div>

            {/* Button */}
            <Button
              onClick={handleSuccessButtonClick}
              className="w-full bg-[#D72E16] hover:bg-[#B8251A] text-white font-medium py-3 h-11 rounded-full transition-colors duration-200"
            >
              {isPast ? t('calendar.add_notes_to_training') : t('common.close')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[90vw] max-w-xl bg-white rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogTitle className="sr-only">{mode === 'edit' ? 'Edit Training' : 'Add Training'}</DialogTitle>
        <SossietyDialogHeader title={mode === 'edit' ? t('calendar.edit_training') : t('calendar.add_training')} titleClassName="text-lg">
          <SossietyDialogClose onClick={handleClose} />
        </SossietyDialogHeader>

        <div className="space-y-4">
          {/* Type of Training - Toggle */}
          <div>
            <Label className="text-black">{t('calendar.training_type')}*</Label>
            <div className="flex bg-gray-100 rounded-lg p-1 w-full">
              <button
                type="button"
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  form.training_type === 'Group Class'
                    ? 'bg-[#D72E16] text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setForm(prev => ({ ...prev, training_type: 'Group Class' }))}
              >
                {t('calendar.group_class')}
              </button>
              <button
                type="button"
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  form.training_type === 'Private Class'
                    ? 'bg-[#D72E16] text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setForm(prev => ({ ...prev, training_type: 'Private Class' }))}
              >
                {t('calendar.private_class')}
              </button>
            </div>
          </div>

          {/* Modality - Gi/No-Gi Toggle */}
          <div>
            <Label className="text-black">{t('calendar.modality')}*</Label>
            <div className="flex bg-gray-100 rounded-lg p-1 w-full">
              <button
                type="button"
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  form.modality === 'Gi'
                    ? 'bg-[#D72E16] text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setForm(prev => ({ ...prev, modality: 'Gi' }))}
              >
                Gi
              </button>
              <button
                type="button"
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  form.modality === 'No-Gi'
                    ? 'bg-[#D72E16] text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setForm(prev => ({ ...prev, modality: 'No-Gi' }))}
              >
                No-Gi
              </button>
            </div>
          </div>

          {/* Category (Optional) */}
          <div>
            <Label className="text-black">{t('calendar.category')} ({t('common.optional')})</Label>
            <div className="w-full border border-black rounded-md">
              <Select 
                value={form.category} 
                onValueChange={(value) => setForm(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger className="w-full text-black !ring-0 !outline-none !shadow-none border-0 focus:!ring-0 focus:!outline-none focus:!shadow-none bg-transparent relative [&_[data-radix-select-icon]]:hidden text-sm">
                  <SelectValue placeholder={t('calendar.select_category')} />
                </SelectTrigger>
                <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] bg-white border-0 py-2 space-y-1 max-h-60">
                  {TRAINING_CATEGORIES.map((category) => (
                    <SelectItem key={category} value={category} className="text-black text-sm">
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-2">
            <div className="w-full">
              <Label htmlFor="date" className="text-black text-sm">{t('calendar.date')}*</Label>
              <Input
                id="date"
                type="date"
                value={form.date}
                onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
                className="text-black border-black bg-white text-sm w-full"
                style={{ minWidth: '0' }}
              />
            </div>
            <div className="w-full">
              <Label htmlFor="time" className="text-black text-sm">{t('calendar.time_field')}*</Label>
              <div className="relative w-full">
                <Input
                  id="time"
                  type="text"
                  value={form.time} 
                  onChange={(e) => {
                    let value = e.target.value;
                    // Allow user to type freely, but we'll validate the format
                    setForm(prev => ({ ...prev, time: value }));
                  }}
                  onBlur={(e) => {
                    // On blur, try to format the time to AM/PM if it's a valid time
                    const value = e.target.value;
                    const formatted = formatTimeToAMPM(value);
                    if (formatted) {
                      setForm(prev => ({ ...prev, time: formatted }));
                    }
                  }}
                  className="text-black border-black bg-white text-sm pr-10 w-full"
                  style={{ minWidth: '0' }}
                  list="time-options"
                  autoComplete="off"
                />
                <datalist id="time-options">
                    {TIME_OPTIONS.map((time) => (
                    <option key={time} value={time} />
                  ))}
                </datalist>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                  AM/PM
                </div>
              </div>
            </div>
          </div>

          {/* Duration */}
          <div>
            <Label htmlFor="duration" className="text-black">
              {t('calendar.duration')} ({t('calendar.time.minutes')})*
            </Label>
            <Input
              id="duration"
              type="number"
              value={form.duration || ''}
              onChange={handleDurationChange}
              className="text-black border-black bg-white"
              min="15"
              max="480"
              step="15"
              placeholder="60"
            />
          </div>

          {/* Location/Academy */}
          <div>
            <Label className="text-black">{t('calendar.academy')}</Label>
            {academyLoading ? (
              <div className="w-full h-10 bg-gray-200 animate-pulse rounded-md" />
            ) : (
              <AcademyAutocomplete
                value={academyValue}
                onChange={handleAcademyChange}
                placeholder={t('calendar.search_academy_placeholder')}
                className="text-black border-black bg-white"
                containerClassName="w-full"
              />
            )}
          </div>

          {/* Instructor */}
          <div>
            <Label className="text-black">{t('calendar.instructor')}</Label>
            <InstructorAutocomplete
              value={instructorValue || null}
              onChange={handleInstructorChange}
              placeholder={t('calendar.search_instructor_placeholder')}
              className="text-black border-black bg-white"
              containerClassName="w-full"
            />
            
            {/* Selected Instructor Display */}
            {instructorValue && typeof instructorValue === 'object' && (
              <div className="mt-3">
                <InstructorDisplay
                  instructor={{
                    id: instructorValue.id,
                    name: instructorValue.name || instructorValue.full_name || '',
                    username: instructorValue.username,
                    profile_picture_url: instructorValue.profile_picture_url,
                    belt_rank: instructorValue.belt_rank
                  }}
                  showRemoveButton={true}
                  onRemove={() => handleInstructorChange(null)}
                  size="md"
                  className="p-2 rounded-lg"
                />
              </div>
            )}
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid() || createTrainingMutation.isPending || updateTrainingMutation.isPending}
            className="w-full bg-[#D72E16] hover:bg-[#B8251A] text-white font-medium py-3 h-11 rounded-full transition-colors duration-200 disabled:opacity-50"
          >
            {mode === 'edit' 
              ? (updateTrainingMutation.isPending ? t('common.loading') : t('common.update'))
              : (createTrainingMutation.isPending ? t('common.loading') : t('common.create'))
            }
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 